/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { createChatMarkdownComponents } from './chat/chatMarkdownComponents.jsx';
import { getCommandIntelligenceReasoningStyles } from './CommandIntelligenceReasoning.styles';
import PsychologyOutlined from '@mui/icons-material/PsychologyOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Collapse, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const CommandIntelligenceReasoning = ({
  isThinkingPhase = false,
  reasoningText = '',
  durationSeconds: durationSecondsProp,
  defaultOpen,
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const styles = useMemo(
    () => getCommandIntelligenceReasoningStyles(theme, { isThinkingPhase }),
    [isThinkingPhase, theme]
  );
  const markdownComponents = useMemo(() => createChatMarkdownComponents(theme, { density: 'compact' }), [theme]);

  const streamStartedAtRef = useRef(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [open, setOpen] = useState(defaultOpen ?? false);
  const [userToggled, setUserToggled] = useState(false);

  const hasReasoningText = Boolean(reasoningText?.trim());

  useLayoutEffect(() => {
    if (isThinkingPhase) {
      if (!streamStartedAtRef.current) {
        streamStartedAtRef.current = Date.now();
      }

      return;
    }

    if (streamStartedAtRef.current) {
      const seconds = Math.max(1, Math.ceil((Date.now() - streamStartedAtRef.current) / 1000));

      setElapsedSeconds(seconds);
      streamStartedAtRef.current = null;
    }
  }, [isThinkingPhase]);

  useEffect(() => {
    if (isThinkingPhase) {
      if (!userToggled) {
        setOpen(true);
      }

      const intervalId = window.setInterval(() => {
        if (streamStartedAtRef.current) {
          setElapsedSeconds(Math.max(0, Math.floor((Date.now() - streamStartedAtRef.current) / 1000)));
        }
      }, 1000);

      return () => window.clearInterval(intervalId);
    }

    if (!userToggled && !hasReasoningText) {
      const timeoutId = window.setTimeout(() => setOpen(false), 400);

      return () => window.clearTimeout(timeoutId);
    }

    return undefined;
  }, [hasReasoningText, isThinkingPhase, userToggled]);

  const durationSeconds = durationSecondsProp ?? elapsedSeconds;

  const triggerLabel = useMemo(() => {
    if (isThinkingPhase) {
      return intl.formatMessage({ id: 'command_intelligence_reasoning_thinking' });
    }

    if (durationSeconds > 0) {
      return intl.formatMessage(
        { id: 'command_intelligence_reasoning_thought_for_seconds' },
        { seconds: durationSeconds }
      );
    }

    return intl.formatMessage({ id: 'command_intelligence_reasoning_thinking' });
  }, [durationSeconds, intl, isThinkingPhase]);

  const handleToggle = useCallback(() => {
    setUserToggled(true);
    setOpen(previous => !previous);
  }, []);

  const showContent = hasReasoningText || (isThinkingPhase && open);

  if (!isThinkingPhase && !hasReasoningText && durationSeconds <= 0) {
    return null;
  }

  return (
    <Box sx={styles.root}>
      <Box component="button" type="button" onClick={handleToggle} sx={styles.trigger}>
        <PsychologyOutlined sx={styles.triggerIcon} aria-hidden />
        <Box component="span" sx={styles.triggerLabel}>
          {triggerLabel}
        </Box>
        <ExpandMore sx={[styles.chevron, open ? styles.chevronOpen : null]} aria-hidden />
      </Box>

      <Collapse in={open && showContent}>
        <Box sx={styles.content}>
          {hasReasoningText ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {reasoningText}
            </ReactMarkdown>
          ) : (
            <Box component="p" sx={styles.waitingText}>
              {intl.formatMessage({ id: 'command_intelligence_reasoning_waiting' })}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default memo(CommandIntelligenceReasoning);
